import React from "react";
import { Box, Grid, Paper, Stack, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import Brand from "../Brand";
import Logo from "../Logo";
import authOperationsIllustration from "../../assets/auth-operations-illustration.svg";
import brand from "../../inc/branding.json";

const highlights = [
  "Role-based dashboards for clients, contractors, and operations",
  "Secure onboarding, billing, and audit visibility",
  "Transparent workflows from request to payment",
];

const OperationsPanelArtwork = () => (
  <Box
    aria-hidden="true"
    sx={{
      position: "absolute",
      inset: 0,
      overflow: "hidden",
      pointerEvents: "none",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        opacity: 0.28,
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.18) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.18) 1px, transparent 1px)",
        backgroundSize: "64px 64px",
        maskImage: "linear-gradient(180deg, rgba(0,0,0,0.98), rgba(0,0,0,0.72) 52%, rgba(0,0,0,0.28) 100%)",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        width: 420,
        height: 420,
        borderRadius: "50%",
        top: "10%",
        right: "-22%",
        background: "radial-gradient(circle, rgba(120,216,160,0.28), rgba(120,216,160,0.05) 42%, transparent 68%)",
        filter: "blur(2px)",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        width: 360,
        height: 360,
        borderRadius: "50%",
        top: "3%",
        left: "-18%",
        background: "radial-gradient(circle, rgba(25,118,210,0.36), rgba(25,118,210,0.08) 46%, transparent 70%)",
        filter: "blur(2px)",
      }}
    />
    <Box
      sx={{
        position: "absolute",
        top: { md: "10%", lg: "11%" },
        left: "50%",
        width: { md: "82%", lg: "78%" },
        maxWidth: 440,
        transform: "translateX(-50%)",
        opacity: 0.88,
        filter: "drop-shadow(0 28px 80px rgba(25,118,210,0.28))",
        "& img": {
          width: "100%",
          height: "auto",
          display: "block",
        },
      }}
    >
      <Box component="img" src={authOperationsIllustration} alt="" />
    </Box>
    <Box
      sx={{
        position: "absolute",
        left: "9%",
        right: "9%",
        top: { md: "48%", lg: "50%" },
        height: 1,
        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.24), transparent)",
      }}
    />
  </Box>
);

const AuthPageFrame = ({ eyebrow, title, subtitle, children, maxWidth = 430 }) => {
  const dba = brand.brandConfig?.config?.dba || "";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        minWidth: "100vw",
        width: "100%",
        overflow: "hidden",
        bgcolor: "#f8fafd",
        background:
          "radial-gradient(circle at 8% 10%, rgba(25,118,210,0.18), transparent 30%), radial-gradient(circle at 72% 96%, rgba(5,105,13,0.16), transparent 32%), linear-gradient(135deg, #f7fbff 0%, #ffffff 42%, #f0f8f2 100%)",
      }}
    >
      <Grid container sx={{ minHeight: "100vh", minWidth: "100vw" }}>
        <Grid
          item
          xs={12}
          md={7}
          lg={7.5}
          sx={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: { xs: 2, sm: 3, md: 4, lg: 5 },
            py: { xs: 2, md: 3 },
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
            }}
          />
          <Box sx={{ width: "100%", maxWidth, position: "relative", zIndex: 1 }}>
            <Stack spacing={1.5}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Logo size={30} />
                <Brand width={90} height={38} />
              </Box>

              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2, sm: 2.25, md: 2.5 },
                  borderRadius: 3,
                  borderColor: "rgba(218,220,224,0.9)",
                  boxShadow: "0 20px 60px rgba(60,64,67,0.12)",
                  overflow: "hidden",
                  position: "relative",
                  backdropFilter: "blur(10px)",
                  backgroundColor: "rgba(255,255,255,0.92)",
                  "&:before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 5,
                    background: "linear-gradient(90deg, #05690D, #1976D2)",
                  },
                  "& .MuiTextField-root": {
                    marginTop: 0,
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                  },
                  "& .MuiInputBase-input": {
                    py: 1.05,
                    fontSize: 14,
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: 14,
                  },
                }}
              >
                <Stack spacing={1.6}>
                  <Box sx={{ textAlign: "center" }}>
                    {!!eyebrow && (
                      <Typography variant="caption" sx={{ color: "primary.main", fontWeight: 850, letterSpacing: 0.9, textTransform: "uppercase" }}>
                        {eyebrow}
                      </Typography>
                    )}
                    <Typography variant="h4" sx={{ fontWeight: 850, mt: 0.35, color: "#30343b", letterSpacing: "-0.03em", fontSize: { xs: 27, md: 29 } }}>
                      {title}
                    </Typography>
                    {!!subtitle && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.45, fontSize: 14 }}>
                        {subtitle}
                      </Typography>
                    )}
                  </Box>

                  {children}
                </Stack>
              </Paper>
            </Stack>
          </Box>
        </Grid>

        <Grid
          item
          xs={12}
          md={5}
          lg={4.5}
          sx={{
            display: { xs: "none", md: "block" },
            minHeight: "100vh",
            position: "relative",
          }}
        >
          <Box
            sx={{
              position: "fixed",
              top: 0,
              right: 0,
              bottom: 0,
              width: { md: "41.6666667vw", lg: "37.5vw" },
              minHeight: "100vh",
              display: "flex",
              alignItems: "stretch",
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                width: "100%",
                minHeight: "100vh",
                borderRadius: 0,
                overflow: "hidden",
                position: "relative",
                border: "none",
                color: "#fff",
                background:
                  "radial-gradient(circle at 18% 14%, rgba(25,118,210,0.42), transparent 28%), radial-gradient(circle at 88% 22%, rgba(120,216,160,0.24), transparent 28%), linear-gradient(145deg, #07152F 0%, #0B1E3F 42%, #07111F 100%)",
              }}
            >
              <OperationsPanelArtwork />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(180deg, rgba(3,7,18,0.00), rgba(3,7,18,0.34) 48%, rgba(3,7,18,0.82) 100%)",
                }}
              />
              <Stack
                spacing={2.2}
                sx={{
                  position: "absolute",
                  top: { md: "51%", lg: "53%" },
                  left: 0,
                  right: 0,
                  px: { md: 3.5, lg: 4.5 },
                  pb: { md: 3.5, lg: 4.5 },
                }}
              >
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 850, letterSpacing: "-0.03em", fontSize: { md: 30, lg: 34 } }}>
                    Work smarter with {dba}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: "rgba(255,255,255,0.82)", lineHeight: 1.65 }}>
                    A cleaner operating layer for teams, contractors, invoices, and approvals.
                  </Typography>
                </Box>
                <Stack spacing={1}>
                  {highlights.map((item) => (
                    <Stack key={item} direction="row" spacing={1.25} alignItems="center">
                      <CheckCircleOutlineIcon sx={{ fontSize: 20, color: "#9BE7B0" }} />
                      <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)" }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            </Paper>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AuthPageFrame;
